import { useState } from "react";
import { Link } from "wouter";
import { Plus, UserPlus, User as UserIcon, Pencil, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCreatePlayer,
  useRoster,
  useMe,
  useUpdatePlayer,
  useDeletePlayer,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import type { Player, Me } from "@/lib/types";

export default function PlayersList() {
  const { data: players = [], isLoading } = useRoster();
  const { data: me } = useMe();
  const create = useCreatePlayer();

  // Only org owners/admins can add or edit roster entries. Members can still
  // edit their own player (handled at the API layer; UI hides Edit otherwise).
  const myRole =
    me?.orgs.find(
      (o) =>
        o.orgSlug ===
        (typeof window !== "undefined"
          ? localStorage.getItem("activeOrgSlug")
          : null),
    )?.role ?? me?.orgs[0]?.role ?? null;
  const canManage =
    me?.isPlatformAdmin || myRole === "owner" || myRole === "admin";

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLoc] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return;
    try {
      await create.mutateAsync({
        name: name.trim(),
        email: email.trim() || undefined,
        location: location.trim() || undefined,
      });
      setName("");
      setEmail("");
      setLoc("");
      setShowForm(false);
    } catch (err) {
      setError((err as ApiError).message ?? "Could not add player");
    }
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
            Roster
          </h1>
          <p className="text-muted-foreground text-sm">
            Everyone who plays in this org. Add placeholders for people who
            haven't signed up yet — give them an email later when you know it.
          </p>
        </div>
        {canManage && !showForm && (
          <Button onClick={() => setShowForm(true)} size="sm">
            <Plus size={16} /> Add player
          </Button>
        )}
      </header>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add player</CardTitle>
            <CardDescription>
              Name is required. Email is optional — if you add it later, the
              owner of that email can claim this profile by signing in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Email{" "}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  Auto-claims when she signs in. If she already has an account,
                  she'll get an invite to confirm.
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Location (optional)
                </label>
                <Input
                  value={location}
                  onChange={(e) => setLoc(e.target.value)}
                  placeholder="Brooklyn, NY"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending || !name.trim()}>
                  {create.isPending ? "Adding…" : "Add player"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading roster…</p>
      ) : players.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <UserPlus className="mx-auto text-muted-foreground" size={32} />
            <p className="text-sm text-muted-foreground">
              No players yet. Add yourself or anyone you play with.
            </p>
            {canManage && !showForm && (
              <Button onClick={() => setShowForm(true)}>
                <Plus size={16} /> Add the first player
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {players.map((p) => (
            <PlayerRow
              key={p.id}
              player={p}
              me={me ?? null}
              canManage={!!canManage}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PlayerRow({
  player,
  me,
  canManage,
}: {
  player: Player;
  me: Me | null;
  canManage: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const isYou = !!(me && player.userId && player.userId === me.id);
  // Members can always edit their own row; admins/owners can edit anyone.
  const canEditThis = canManage || isYou;

  if (editing) {
    return (
      <li className="p-3">
        <PlayerEditForm
          player={player}
          canManage={canManage}
          onDone={() => setEditing(false)}
        />
      </li>
    );
  }

  const initials = player.name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <li className="flex items-center gap-3 p-3 group">
      <div className="h-10 w-10 rounded-full bg-muted overflow-hidden border border-border flex items-center justify-center text-sm font-display">
        {player.avatarUrl ? (
          <img
            src={player.avatarUrl}
            alt={player.name}
            className="h-full w-full object-cover"
          />
        ) : (
          initials || <UserIcon size={16} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Name links to the player profile page (per-player W/L,
              head-to-head, accuracy trend, recent games). Falls
              through to a plain span if slug is somehow missing. */}
          {player.slug ? (
            <Link
              href={`/dashboard/players/${player.slug}`}
              className="font-medium truncate hover:underline"
            >
              {player.name}
            </Link>
          ) : (
            <span className="font-medium truncate">{player.name}</span>
          )}
          {isYou && (
            <span className="text-[10px] font-display uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
              you
            </span>
          )}
          {!player.userId && (
            <span className="text-[10px] font-display uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              {player.email ? "awaiting sign-in" : "placeholder"}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          @{player.slug}
          {player.email ? ` · ${player.email}` : ""}
          {player.location ? ` · ${player.location}` : ""}
        </div>
      </div>
      {canEditThis && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditing(true)}
          className="opacity-60 group-hover:opacity-100"
        >
          <Pencil size={14} />
          <span className="sr-only">Edit</span>
        </Button>
      )}
    </li>
  );
}

function PlayerEditForm({
  player,
  canManage,
  onDone,
}: {
  player: Player;
  canManage: boolean;
  onDone: () => void;
}) {
  const update = useUpdatePlayer();
  const del = useDeletePlayer();
  const [name, setName] = useState(player.name);
  const [slug, setSlug] = useState(player.slug);
  const [emailInput, setEmail] = useState(player.email ?? "");
  const [location, setLoc] = useState(player.location ?? "");
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Delete is available only for placeholders, and only to org admins/owners.
  // Backend additionally refuses if the player has any games, teams, or
  // event registrations attached.
  const canDelete = canManage && !player.userId;
  // Slug is editable by admins for placeholders + linked players, and by
  // the linked user themselves for their own row.
  const canEditSlug = canManage || !player.userId;

  async function save() {
    setError(null);
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    try {
      await update.mutateAsync({
        id: player.id,
        input: {
          name: name.trim() !== player.name ? name.trim() : undefined,
          slug: slug.trim() !== player.slug ? slug.trim() : undefined,
          email:
            emailInput.trim() !== (player.email ?? "")
              ? emailInput.trim() || null
              : undefined,
          location:
            location.trim() !== (player.location ?? "")
              ? location.trim() || null
              : undefined,
        },
      });
      onDone();
    } catch (e) {
      setError((e as ApiError).message ?? "Could not save");
    }
  }

  async function remove() {
    setError(null);
    try {
      await del.mutateAsync(player.id);
      onDone();
    } catch (e) {
      setError((e as ApiError).message ?? "Could not remove player");
      setConfirmingDelete(false);
    }
  }

  return (
    <div className="space-y-3 bg-muted/30 rounded-md p-3 border border-border">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">@-slug</label>
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            disabled={!canEditSlug}
            placeholder="hank"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Email</label>
          <Input
            type="email"
            value={emailInput}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="optional"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Location</label>
          <Input
            value={location}
            onChange={(e) => setLoc(e.target.value)}
            placeholder="optional"
          />
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          {canDelete && !confirmingDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:bg-destructive/10"
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2 size={14} /> Remove
            </Button>
          )}
          {canDelete && confirmingDelete && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Remove this player?</span>
              <Button
                size="sm"
                variant="destructive"
                onClick={remove}
                disabled={del.isPending}
              >
                {del.isPending ? "Removing…" : "Yes, remove"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmingDelete(false)}
              >
                Keep
              </Button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setError(null);
              onDone();
            }}
          >
            <X size={14} /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

